import type { Actor, Incident } from '@/types/map'
import { ReasonChips } from '../ProjectionProvenance'
import '../map-console.css'

export interface TopAttackersPanelProps {
  actors: Actor[]
  incidents: Incident[]
  onActorClick: (actorId: string) => void
  isLoading?: boolean
}

const MAX_ROWS = 5

export function TopAttackersPanel({
  actors,
  incidents,
  onActorClick,
  isLoading = false,
}: TopAttackersPanelProps) {
  if (isLoading) {
    return (
      <section
        aria-busy="true"
        data-testid="top-attackers-skeleton"
        className="soc-panel p-3"
      >
        <div className="mb-2 h-3 w-24 animate-pulse rounded bg-[color:var(--surface-tertiary)]" />
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="h-4 w-full animate-pulse rounded bg-[color:var(--surface-tertiary)]"
            />
          ))}
        </div>
      </section>
    )
  }

  const counts = new Map<string, number>()
  const groupCounts = new Map<string, { name: string; count: number; reasons: Incident['unknownReasons'] }>()
  for (const inc of incidents) {
    if (inc.actorId) counts.set(inc.actorId, (counts.get(inc.actorId) ?? 0) + 1)
    else if (inc.ransomwareGroupId && inc.ransomwareGroupName) {
      const current = groupCounts.get(inc.ransomwareGroupId)
      groupCounts.set(inc.ransomwareGroupId, {
        name: inc.ransomwareGroupName,
        count: (current?.count ?? 0) + 1,
        reasons: [...new Set([...(current?.reasons ?? []), ...(inc.unknownReasons ?? [])])],
      })
    }
  }

  const ranked = [...actors].sort(
    (a, b) => (counts.get(b.id) ?? 0) - (counts.get(a.id) ?? 0),
  )
  const top = ranked.slice(0, MAX_ROWS)
  const overflow = ranked.length - top.length
  const max = Math.max(counts.get(top[0]?.id ?? '') ?? 0, 1)
  const topGroups = [...groupCounts.entries()]
    .map(([id, group]) => ({ id, ...group }))
    .sort((a, b) => b.count - a.count)
    .slice(0, MAX_ROWS)

  return (
    <section className="soc-panel overflow-hidden">
      <header className="soc-panel-head flex items-center justify-between px-3 py-2">
        <h3 className="flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.18em] text-[color:var(--text-secondary)]">
          <span className="text-[color:var(--interactive-primary)]" aria-hidden>
            ▸
          </span>
          Top Attackers
        </h3>
        {ranked.length > 0 && (
          <span className="font-mono text-[10px] tracking-widest text-[color:var(--text-tertiary)]">
            N={ranked.length}
          </span>
        )}
      </header>
      {ranked.length === 0 ? (
        <p
          data-testid="top-attackers-empty"
          className="px-3 py-3 text-xs text-[color:var(--text-tertiary)]"
        >
          {topGroups.length > 0 ? 'No canonical actors observed in range' : 'No attackers observed in range'}
        </p>
      ) : (
        <>
          <ul className="px-2 py-1.5">
            {top.map((a) => {
              const count = counts.get(a.id) ?? 0
              return (
                <li
                  key={a.id}
                  data-testid="attacker-row"
                  onClick={() => onActorClick(a.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      onActorClick(a.id)
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  className="group cursor-pointer rounded-md px-1.5 py-1.5 transition-colors hover:bg-[color:var(--surface-tertiary)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--interactive-primary)]"
                >
                  <div className="flex items-center justify-between gap-2 text-sm">
                    <span className="truncate text-[color:var(--text-primary)] group-hover:text-[color:var(--interactive-primary)]">
                      {a.name}
                    </span>
                    <span className="flex shrink-0 items-center gap-2">
                      <span className="font-mono text-xs text-[color:var(--text-secondary)]">
                        {count}
                      </span>
                    </span>
                  </div>
                  <div className="mt-1 h-1 overflow-hidden rounded-full bg-[color:var(--surface-tertiary)]">
                    <div
                      className="h-full rounded-full transition-[width] duration-200"
                      style={{
                        width: `${Math.max((count / max) * 100, 4)}%`,
                        background: 'var(--interactive-primary)',
                      }}
                    />
                  </div>

                </li>
              )
            })}
          </ul>
          {overflow > 0 && (
            <p className="px-3 pb-2 text-[10px] uppercase tracking-widest text-[color:var(--text-tertiary)]">
              +{overflow} more
            </p>
          )}
        </>
      )}
      {topGroups.length > 0 && (
        <div className="border-t border-[color:var(--border-subtle)] px-3 py-2">
          <p className="font-mono text-[9px] uppercase tracking-[0.16em] text-[color:var(--text-tertiary)]">
            Groups needing attribution
          </p>
          <ul className="mt-1 space-y-1">
            {topGroups.map((group) => (
              <li
                key={group.id}
                data-testid="unlinked-ransomware-group-row"
                className="flex flex-col gap-1 text-xs"
              >
                <span className="flex items-center justify-between gap-2">
                  <span className="truncate text-[color:var(--text-secondary)]">{group.name}</span>
                  <span className="font-mono text-[10px] text-[color:var(--text-tertiary)]">{group.count}</span>
                </span>
                <ReasonChips reasons={group.reasons} max={2} label={`Reasons for ${group.name}`} />
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  )
}
